import * as THREE from 'three';
import { accentHex, isDarkTheme, nodeColor, nodeRadius } from './graphTheme';
import { linkLabelCandidates } from './graphLinkLabels';
import { DIM, FADE_TAU_MS, MAX_LABELS, curvePoint, savePositions } from './graphCanvasConfig';

/** The WebGL scene inside GraphCanvas's effect: render loop, colours and emphasis, labels, and loading a graph (setGraph, layout ticks). A plain factory called once from the effect; reassigned values live on the shared live object. */
export function graphScene({
  s,
  camera,
  worker,
  scene,
  live,
  host,
  props,
  labelHost,
  renderer,
  nodeMaterial,
  edgeUniforms,
  edgeMaterial,
  LINK_SEG,
  linkGeom,
  selectedIdRef,
  cancelLink,
  fitView,
}) {
    const requestRender = () => {
      if (!live.frame) live.frame = requestAnimationFrame(draw);
    };

    const resize = () => {
      const w = host.clientWidth || 1;
      const h = host.clientHeight || 1;
      s.width = w;
      s.height = h;
      renderer.setSize(w, h, false);
      renderer.domElement.style.width = `${w}px`;
      renderer.domElement.style.height = `${h}px`;
      applyCamera();
    };

    const applyCamera = () => {
      camera.left = -s.width / 2 / s.zoom;
      camera.right = s.width / 2 / s.zoom;
      camera.top = s.height / 2 / s.zoom;
      camera.bottom = -s.height / 2 / s.zoom;
      camera.updateProjectionMatrix();
      nodeMaterial.uniforms.uZoom.value = s.zoom;
      edgeUniforms.uZoom.value = s.zoom;
      requestRender();
    };

    const toWorld = (sx, sy) => ({
      x: (sx - s.width / 2) / s.zoom + camera.position.x,
      y: -(sy - s.height / 2) / s.zoom + camera.position.y,
    });

    const posAttr = () => live.points?.geometry.getAttribute('position');

    const pick = (sx, sy) => {
      const pos = posAttr();
      if (!pos) return -1;
      const w = toWorld(sx, sy);
      const arr = pos.array;
      let best = -1;
      let bestD = Infinity;
      const slack = 4 / s.zoom;
      for (let i = 0; i < s.nodes.length; i += 1) {
        const dx = arr[i * 3] - w.x;
        const dy = arr[i * 3 + 1] - w.y;
        const d = dx * dx + dy * dy;
        const r = s.radius[i] + slack;
        if (d < r * r && d < bestD) {
          best = i;
          bestD = d;
        }
      }
      return best;
    };

    // ── colours / emphasis ─────────────────────────────────────────────────
    const applyColors = () => {
      if (!live.points) return;
      s.dark = isDarkTheme();
      const accent = new THREE.Color(accentHex());
      nodeMaterial.uniforms.uRingColor.value = accent;
      edgeUniforms.uAccent.value = accent;
      const colors = live.points.geometry.getAttribute('aColor');
      const c = new THREE.Color();
      s.nodes.forEach((n, i) => {
        c.set(nodeColor(n, s.dark));
        colors.setXYZ(i, c.r, c.g, c.b);
      });
      colors.needsUpdate = true;
      const edgeColors = live.lines.geometry.getAttribute('aColor');
      // Light enough to read on #111113 (owner: dark-mode links were invisible).
      const neutral = new THREE.Color(s.dark ? '#a3a3b8' : '#94a3b8');
      const per = s.seg * 2;
      s.edges.forEach((e, i) => {
        const col = e.origin === 'explicit' ? accent : neutral;
        for (let v = 0; v < per; v += 1) edgeColors.setXYZ(i * per + v, col.r, col.g, col.b);
      });
      edgeColors.needsUpdate = true;
      const lc = linkGeom.getAttribute('aColor');
      for (let v = 0; v < LINK_SEG * 2; v += 1) lc.setXYZ(v, accent.r, accent.g, accent.b);
      lc.needsUpdate = true;
      requestRender();
    };

    /** The node whose neighbourhood is in focus: link source, else hover. */
    const focusIndex = () => (s.linkFrom >= 0 ? s.linkFrom : s.hovered >= 0 ? s.hovered : s.externalHover);

    const applyEmphasis = () => {
      const focus = focusIndex();
      const hl = s.highlight;
      for (let i = 0; i < s.nodes.length; i += 1) {
        let a = 1;
        if (focus >= 0 && s.linkFrom < 0) a = i === focus || s.neighbours[focus].has(i) ? 1 : DIM;
        else if (hl) a = hl.has(s.nodes[i].id) ? 1 : DIM;
        s.alphaTarget[i] = a;
      }
      const crowd = s.edges.length > 1500 ? 0.6 : 1;
      let anyMarch = false;
      const march = live.lines?.geometry.getAttribute('aMarch');
      const per = s.seg * 2;
      for (let i = 0; i < s.edges.length; i += 1) {
        const e = s.edges[i];
        const a = s.edgeEnds[i * 2];
        const b = s.edgeEnds[i * 2 + 1];
        // Dotted 1px lines read lighter than solid ones: keep them strong.
        let t = (e.origin === 'explicit' ? 1 : s.dark ? 0.85 : 0.9) * crowd;
        let m = 0;
        if (focus >= 0 && s.linkFrom < 0) {
          const incident = a === focus || b === focus;
          t = incident ? 1 : t * DIM;
          m = incident ? 1 : 0;
        } else if (hl) t = hl.has(s.nodes[a].id) && hl.has(s.nodes[b].id) ? t : t * DIM;
        s.edgeAlphaTarget[i] = t;
        if (s.march[i] !== m && march) {
          s.march[i] = m;
          for (let v = 0; v < per; v += 1) march.array[i * per + v] = m;
          march.needsUpdate = true;
        }
        if (m) anyMarch = true;
      }
      s.marching = anyMarch;
      const rings = live.points?.geometry.getAttribute('aRing');
      if (rings) {
        for (let i = 0; i < s.nodes.length; i += 1) rings.setX(i, i === s.selected ? 1 : 0);
        rings.needsUpdate = true;
      }
      requestRender();
    };

    // ── the frame ──────────────────────────────────────────────────────────
    live.lastDraw = 0;
    const draw = () => {
      live.frame = 0;
      const now = performance.now();
      // Clamp dt: the first frame after a pause must not skip the fade.
      const dt = live.lastDraw ? Math.min(50, now - live.lastDraw) : 16;
      live.lastDraw = now;
      const k = 1 - Math.exp(-dt / FADE_TAU_MS);
      if (!live.points) {
        renderer.render(scene, camera);
        return;
      }
      // Ease only what still differs, and upload a buffer only if it changed:
      // while links merely march, a frame is just the uTime uniform.
      let animating = false;
      const nodeAlpha = live.points.geometry.getAttribute('aAlpha');
      let nodesChanged = false;
      for (let i = 0; i < s.alpha.length; i += 1) {
        if (s.alpha[i] === s.alphaTarget[i]) continue;
        const d = s.alphaTarget[i] - s.alpha[i];
        if (Math.abs(d) > 0.004) {
          s.alpha[i] += d * k;
          animating = true;
        } else s.alpha[i] = s.alphaTarget[i];
        nodeAlpha.array[i] = s.alpha[i];
        nodesChanged = true;
      }
      if (nodesChanged) nodeAlpha.needsUpdate = true;
      const edgeAlpha = live.lines.geometry.getAttribute('aAlpha');
      const per = s.seg * 2;
      let edgesChanged = false;
      for (let i = 0; i < s.edgeAlpha.length; i += 1) {
        if (s.edgeAlpha[i] === s.edgeAlphaTarget[i]) continue;
        const d = s.edgeAlphaTarget[i] - s.edgeAlpha[i];
        if (Math.abs(d) > 0.004) {
          s.edgeAlpha[i] += d * k;
          animating = true;
        } else s.edgeAlpha[i] = s.edgeAlphaTarget[i];
        const a = s.edgeAlpha[i];
        const base = i * per;
        for (let v = 0; v < per; v += 1) edgeAlpha.array[base + v] = a;
        edgesChanged = true;
      }
      if (edgesChanged) edgeAlpha.needsUpdate = true;
      edgeUniforms.uTime.value = (performance.now() - s.startedAt) / 1000;
      if (s.linkFrom >= 0) syncLinkLine();
      renderer.render(scene, camera);
      updateLabels();
      // Marching dots (hover / link mode) need frames; nothing else does.
      if (animating || s.marching || s.linkFrom >= 0) requestRender();
      else live.lastDraw = 0;
    };

    // ── labels: a pool, best-connected visible nodes first, fade with zoom ─
    const pool = [];
    const updateLabels = () => {
      const pos = posAttr();
      if (!pos) return;
      const arr = pos.array;
      const focus = focusIndex();
      let used = 0;
      // Occupied screen boxes: a label that would overlap one already placed is
      // skipped (hovered/selected labels are placed first, so they always win).
      const boxes = [];
      const place = (i, opacity, strong, force = false) => {
        if (used >= MAX_LABELS) return;
        const sx = (arr[i * 3] - camera.position.x) * s.zoom + s.width / 2;
        const sy = -(arr[i * 3 + 1] - camera.position.y) * s.zoom + s.height / 2;
        if (sx < -80 || sx > s.width + 80 || sy < -20 || sy > s.height + 20) return;
        const top = sy + s.radius[i] * s.zoom + 4;
        const halfW = Math.min(90, s.nodes[i].label.length * 3.1) + 3;
        const box = [sx - halfW, top - 1, sx + halfW, top + 14];
        if (!force && boxes.some((b) => box[0] < b[2] && box[2] > b[0] && box[1] < b[3] && box[3] > b[1])) return;
        boxes.push(box);
        let el = pool[used];
        if (!el) {
          el = document.createElement('div');
          el.className = 'graph-label';
          labelHost.appendChild(el);
          pool.push(el);
        }
        const text = s.nodes[i].label;
        if (el.textContent !== text) el.textContent = text;
        el.style.transform = `translate(${sx.toFixed(1)}px, ${top.toFixed(1)}px) translateX(-50%)`;
        el.style.opacity = String(opacity * Math.max(s.alpha[i], strong ? 1 : 0));
        el.classList.toggle('graph-label--strong', !!strong);
        el.style.display = '';
        used += 1;
      };
      const shown = new Set();
      if (focus >= 0) {
        place(focus, 1, true, true);
        shown.add(focus);
        if (s.linkFrom < 0) {
          for (const j of s.neighbours[focus]) {
            place(j, 0.95, false);
            shown.add(j);
          }
        }
      }
      if (s.linkFrom >= 0 && s.hovered >= 0 && !shown.has(s.hovered)) {
        place(s.hovered, 1, true, true);
        shown.add(s.hovered);
      }
      if (s.linkFrom >= 0) {
        // The likely targets keep their labels even where they overlap.
        for (const j of linkLabelCandidates(arr, s.nodes.length, s.linkCursor, s.zoom, s.linkFrom)) {
          if (shown.has(j)) continue;
          place(j, 1, false, true);
          shown.add(j);
        }
      }
      if (s.selected >= 0 && !shown.has(s.selected)) {
        place(s.selected, 1, true, true);
        shown.add(s.selected);
      }
      if (focus < 0 || s.linkFrom >= 0) {
        for (const i of s.labelOrder) {
          if (used >= MAX_LABELS) break;
          if (shown.has(i)) continue;
          // Bigger / better connected nodes earn their label at lower zoom.
          const screenR = s.radius[i] * s.zoom;
          const opacity = Math.min(1, Math.max(0, (screenR - 3.5) / 3));
          if (opacity <= 0.02) continue;
          place(i, opacity, false);
        }
      }
      for (let k = used; k < pool.length; k += 1) pool[k].style.display = 'none';
    };

    // ── graph in ───────────────────────────────────────────────────────────
    const setGraph = (graph) => {
      live.generation += 1;
      // A pending save belongs to the old graph's arrays: drop it.
      clearTimeout(live.saveTimer);
      if (live.drag && live.drag.node >= 0) live.drag = null; // the dragged index means another node now
      if (s.linkFrom >= 0) cancelLink();
      const prevPos = posAttr();
      const prevIndex = s.index;
      const prevAlpha = s.alpha;
      const prevEdgeAlpha = new Map(s.edges.map((e, i) => [e.id, s.edgeAlpha[i]]));
      if (prevPos) {
        s.nodes.forEach((n, i) => {
          s.saved[n.id] = [prevPos.array[i * 3], prevPos.array[i * 3 + 1]];
        });
      }

      const nodes = graph?.nodes || [];
      const index = new Map(nodes.map((n, i) => [n.id, i]));
      const edges = (graph?.edges || []).filter((e) => index.has(e.from) && index.has(e.to));
      const n = nodes.length;
      const degree = new Array(n).fill(0);
      const neighbours = nodes.map(() => new Set());
      const edgeEnds = new Uint32Array(edges.length * 2);
      const bend = new Float32Array(edges.length);
      edges.forEach((e, i) => {
        const a = index.get(e.from);
        const b = index.get(e.to);
        edgeEnds[i * 2] = a;
        edgeEnds[i * 2 + 1] = b;
        degree[a] += 1;
        degree[b] += 1;
        neighbours[a].add(b);
        neighbours[b].add(a);
        // Stable per-link curve: the id's hash picks direction and strength,
        // so a link keeps its shape across rebuilds.
        let h = 0;
        for (let k = 0; k < e.id.length; k += 1) h = (Math.imul(h, 31) + e.id.charCodeAt(k)) | 0;
        bend[i] = ((h & 1 ? 1 : -1) * (0.1 + ((h >>> 1) % 7) * 0.012));
      });
      // Fewer segments per curve when there are many links.
      const seg = edges.length > 6000 ? 5 : edges.length > 2000 ? 8 : 14;

      Object.assign(s, {
        nodes,
        edges,
        index,
        degree,
        neighbours,
        edgeEnds,
        bend,
        seg,
        radius: Float32Array.from(nodes, (node, i) => nodeRadius(node, degree[i])),
        alpha: new Float32Array(n),
        alphaTarget: new Float32Array(n).fill(1),
        edgeAlpha: new Float32Array(edges.length),
        edgeAlphaTarget: new Float32Array(edges.length),
        march: new Float32Array(edges.length),
        labelOrder: [...nodes.keys()].sort((a, b) => degree[b] - degree[a] || nodes[a].label.length - nodes[b].label.length),
      });
      // Keep existing nodes and links visible; new ones fade in.
      nodes.forEach((node, i) => {
        const old = prevIndex.get(node.id);
        if (old != null && prevAlpha[old] != null) s.alpha[i] = prevAlpha[old];
      });
      edges.forEach((e, i) => {
        const old = prevEdgeAlpha.get(e.id);
        if (old != null) s.edgeAlpha[i] = old;
      });
      if (s.hovered >= 0) props.current.onHover?.(null);
      s.hovered = -1;
      s.externalHover = -1;
      s.selected = selectedIdRef.current != null ? index.get(selectedIdRef.current) ?? -1 : -1;

      if (live.points) {
        scene.remove(live.points);
        live.points.geometry.dispose();
      }
      if (live.lines) {
        scene.remove(live.lines);
        live.lines.geometry.dispose();
      }

      const positions = new Float32Array(Math.max(n, 1) * 3);
      nodes.forEach((node, i) => {
        const p = s.saved[node.id];
        if (p) {
          positions[i * 3] = p[0];
          positions[i * 3 + 1] = p[1];
        }
      });
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      g.setAttribute('aColor', new THREE.BufferAttribute(new Float32Array(Math.max(n, 1) * 3), 3));
      g.setAttribute('aSize', new THREE.BufferAttribute(Float32Array.from(s.radius.length ? s.radius : [0]), 1));
      g.setAttribute('aAlpha', new THREE.BufferAttribute(new Float32Array(Math.max(n, 1)), 1));
      g.setAttribute('aRing', new THREE.BufferAttribute(new Float32Array(Math.max(n, 1)), 1));
      g.setDrawRange(0, n);
      g.getAttribute('aAlpha').array.set(s.alpha);
      live.points = new THREE.Points(g, nodeMaterial);
      live.points.frustumCulled = false;
      // Transparent objects are depth-sorted by three; pin links under nodes.
      live.points.renderOrder = 1;

      const eg = new THREE.BufferGeometry();
      const verts = Math.max(edges.length, 1) * seg * 2;
      eg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verts * 3), 3));
      eg.setAttribute('aColor', new THREE.BufferAttribute(new Float32Array(verts * 3), 3));
      eg.setAttribute('aAlpha', new THREE.BufferAttribute(new Float32Array(verts), 1));
      eg.setAttribute('aMarch', new THREE.BufferAttribute(new Float32Array(verts), 1));
      eg.setAttribute('aDist', new THREE.BufferAttribute(new Float32Array(verts), 1));
      eg.setDrawRange(0, edges.length * seg * 2);
      {
        const ea = eg.getAttribute('aAlpha').array;
        for (let i = 0; i < edges.length; i += 1) ea.fill(s.edgeAlpha[i], i * seg * 2, (i + 1) * seg * 2);
      }
      live.lines = new THREE.LineSegments(eg, edgeMaterial);
      live.lines.frustumCulled = false;
      live.lines.renderOrder = 0;

      scene.add(live.lines);
      scene.add(live.points);
      syncEdgePositions();
      applyColors();
      applyEmphasis();

      const newCount = nodes.filter((node) => !s.saved[node.id]).length;
      s.settled = false;
      if (n > 0 && newCount > n / 2) {
        s.fitOnSettle = true;
        s.userMoved = false;
      }
      worker.postMessage({
        type: 'set',
        gen: live.generation,
        nodes: nodes.map((node, i) => {
          const p = s.saved[node.id];
          return { id: node.id, degree: degree[i], x: p?.[0], y: p?.[1] };
        }),
        edges: edges.map((e) => ({ from: e.from, to: e.to, rel: e.rel })),
        reheat: newCount > 0,
      });
      if (!s.hasPositions && n > 0 && newCount < n) {
        s.hasPositions = true;
        fitView(false);
      }
    };

    // Curves are rebuilt from node positions on every layout tick: seg segments
    // per link, each vertex carrying its distance along the curve for the dots.
    const tmp = new Float32Array(2);
    const syncEdgePositions = () => {
      const pos = posAttr();
      if (!pos || !live.lines) return;
      const src = pos.array;
      const out = live.lines.geometry.getAttribute('position').array;
      const dist = live.lines.geometry.getAttribute('aDist').array;
      const seg = s.seg;
      for (let i = 0; i < s.edges.length; i += 1) {
        const a = s.edgeEnds[i * 2] * 3;
        const b = s.edgeEnds[i * 2 + 1] * 3;
        const ax = src[a];
        const ay = src[a + 1];
        const bx = src[b];
        const by = src[b + 1];
        const bend = s.bend[i];
        let px = ax;
        let py = ay;
        let acc = 0;
        const base = i * seg * 2;
        for (let k = 0; k < seg; k += 1) {
          curvePoint(ax, ay, bx, by, bend, (k + 1) / seg, tmp, 0);
          const v = base + k * 2;
          out[v * 3] = px;
          out[v * 3 + 1] = py;
          out[(v + 1) * 3] = tmp[0];
          out[(v + 1) * 3 + 1] = tmp[1];
          dist[v] = acc;
          acc += Math.hypot(tmp[0] - px, tmp[1] - py);
          dist[v + 1] = acc;
          px = tmp[0];
          py = tmp[1];
        }
      }
      live.lines.geometry.getAttribute('position').needsUpdate = true;
      live.lines.geometry.getAttribute('aDist').needsUpdate = true;
    };

    const syncLinkLine = () => {
      const pos = posAttr();
      if (!pos || s.linkFrom < 0) return;
      const ax = pos.array[s.linkFrom * 3];
      const ay = pos.array[s.linkFrom * 3 + 1];
      let bx = s.linkCursor.x;
      let by = s.linkCursor.y;
      if (s.hovered >= 0 && s.hovered !== s.linkFrom) {
        bx = pos.array[s.hovered * 3];
        by = pos.array[s.hovered * 3 + 1];
      }
      const out = linkGeom.getAttribute('position').array;
      const dist = linkGeom.getAttribute('aDist').array;
      let px = ax;
      let py = ay;
      let acc = 0;
      for (let k = 0; k < LINK_SEG; k += 1) {
        curvePoint(ax, ay, bx, by, 0.14, (k + 1) / LINK_SEG, tmp, 0);
        const v = k * 2;
        out[v * 3] = px;
        out[v * 3 + 1] = py;
        out[(v + 1) * 3] = tmp[0];
        out[(v + 1) * 3 + 1] = tmp[1];
        dist[v] = acc;
        acc += Math.hypot(tmp[0] - px, tmp[1] - py);
        dist[v + 1] = acc;
        px = tmp[0];
        py = tmp[1];
      }
      linkGeom.getAttribute('position').needsUpdate = true;
      linkGeom.getAttribute('aDist').needsUpdate = true;
    };

    worker.onmessage = (ev) => {
      const msg = ev.data;
      if (msg?.type !== 'tick' || !live.points || msg.gen !== live.generation) return;
      const pos = posAttr();
      const incoming = msg.positions;
      if (incoming.length / 2 !== s.nodes.length) return;
      const arr = pos.array;
      for (let i = 0; i < s.nodes.length; i += 1) {
        arr[i * 3] = incoming[i * 2];
        arr[i * 3 + 1] = incoming[i * 2 + 1];
      }
      pos.needsUpdate = true;
      syncEdgePositions();
      if (!s.hasPositions && s.nodes.length) {
        s.hasPositions = true;
        fitView(false);
      }
      requestRender();
      if (msg.settled && !s.settled) {
        s.settled = true;
        if (s.fitOnSettle && !s.userMoved) fitView(true);
        s.fitOnSettle = false;
        clearTimeout(live.saveTimer);
        live.saveTimer = setTimeout(() => {
          // Read the CURRENT buffers when the timer fires, and MERGE: a filtered
          // view must not erase every other node's remembered position.
          const now = posAttr()?.array;
          if (!now) return;
          const merged = { ...s.saved };
          s.nodes.forEach((node, i) => {
            merged[node.id] = [Math.round(now[i * 3]), Math.round(now[i * 3 + 1])];
          });
          // Forget items that no longer exist anywhere (not just off-screen).
          const known = props.current.knownIds;
          // Vault-backed items vanish while the vault is locked; keep their spot.
          const vaultBacked = (id) => id.startsWith('db_connection:') || id.startsWith('ssh_host:');
          if (known) for (const id of Object.keys(merged)) if (!known.has(id) && !vaultBacked(id)) delete merged[id];
          s.saved = merged;
          savePositions(merged);
        }, 300);
      }
    };

  return {
    requestRender,
    resize,
    applyCamera,
    toWorld,
    posAttr,
    pick,
    applyColors,
    applyEmphasis,
    pool,
    setGraph,
  };
}
